import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { OrgLayout } from '../components/OrgLayout';
import { RecordInfoButton } from '../components/RecordInfoButton';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import type { AcademicYear, BatchGrid, BatchYear, DepartmentDto, Institution, ProgramDto } from '../types/models';

const emptyForm = {
  institutionId: '', departmentId: '', programId: '', code: '', name: '',
  startAcademicYearId: '', endAcademicYearId: '',
  effectiveDate: '', effectiveThruDate: '', batchYearId: '',
};

/** Format an ISO date string ("YYYY-MM-DD") as "DD-MM-YYYY"; blank for null. */
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}-${m}-${y}` : iso;
}

export function BatchPage() {
  const { orgId = '' } = useParams();
  const toast = useToast();

  const [insts, setInsts] = useState<Institution[]>([]);
  const [depts, setDepts] = useState<DepartmentDto[]>([]);
  const [programs, setPrograms] = useState<ProgramDto[]>([]);
  const [batches, setBatches] = useState<BatchGrid[] | null>(null);
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [batchYears, setBatchYears] = useState<BatchYear[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Filter bar (Institution → Department → Program).
  const [filter, setFilter] = useState({ institutionId: '', departmentId: '', programId: '' });

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  async function refresh() {
    try {
      const [i, d, p, b, ay, by] = await Promise.all([
        api.listInstitutions(orgId), api.listDepartments(orgId),
        api.listPrograms(orgId), api.listAllBatches(orgId), api.listAcademicYears(orgId),
        api.listBatchYears(orgId),
      ]);
      setInsts(i); setDepts(d); setPrograms(p); setBatches(b); setAcademicYears(ay); setBatchYears(by);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load batches.');
    }
  }

  // Cascade filtering inside the create/edit form.
  const deptsForForm = useMemo(
    () => depts.filter(d => d.institutionId === form.institutionId),
    [depts, form.institutionId],
  );
  const programsForForm = useMemo(
    () => programs.filter(p => p.departmentId === form.departmentId),
    [programs, form.departmentId],
  );
  // The batch year currently chosen in the form (drives the batch's start/end dates).
  const selectedBatchYear = useMemo(
    () => batchYears.find(y => y.id === form.batchYearId) ?? null,
    [batchYears, form.batchYearId],
  );

  const hasPrograms = programs.length > 0;

  // Filter-bar cascades + the filtered grid.
  const deptsForFilter = useMemo(
    () => depts.filter(d => d.institutionId === filter.institutionId),
    [depts, filter.institutionId],
  );
  const programsForFilter = useMemo(
    () => programs.filter(p => p.departmentId === filter.departmentId),
    [programs, filter.departmentId],
  );

  function setFilterField(key: keyof typeof filter, value: string) {
    setFilter(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'institutionId') { next.departmentId = ''; next.programId = ''; }
      if (key === 'departmentId') { next.programId = ''; }
      return next;
    });
  }

  const filteredBatches = useMemo(
    () => (batches ?? []).filter(b =>
      (!filter.institutionId || b.institutionId === filter.institutionId)
      && (!filter.departmentId || b.departmentId === filter.departmentId)
      && (!filter.programId || b.programId === filter.programId)),
    [batches, filter],
  );

  function setField(key: keyof typeof emptyForm, value: string) {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'institutionId') { next.departmentId = ''; next.programId = ''; }
      if (key === 'departmentId') { next.programId = ''; }
      return next;
    });
  }

  function openCreate() {
    setFormError(null); setEditId(null); setForm({ ...emptyForm }); setShowModal(true);
  }

  function openEdit(b: BatchGrid) {
    setFormError(null);
    setEditId(b.id);
    setForm({
      institutionId: b.institutionId, departmentId: b.departmentId, programId: b.programId,
      code: b.code, name: b.name,
      startAcademicYearId: b.startAcademicYearId ?? '',
      endAcademicYearId: b.endAcademicYearId ?? '',
      effectiveDate: b.effectiveDate ?? '',
      effectiveThruDate: b.effectiveThruDate ?? '',
      batchYearId: b.batchYearId ?? '',
    });
    setShowModal(true);
  }

  // Selecting a batch year auto-fills the batch's start/end (effective) dates from it; clearing it
  // leaves the dates as-is so they can be entered manually.
  function pickBatchYear(id: string) {
    const by = batchYears.find(y => y.id === id);
    setForm(prev => ({
      ...prev,
      batchYearId: id,
      effectiveDate: by ? by.startDate : prev.effectiveDate,
      effectiveThruDate: by ? by.endDate : prev.effectiveThruDate,
    }));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setBusy(true);
    const req = {
      code: form.code.trim(),
      name: form.name.trim(),
      startAcademicYearId: form.startAcademicYearId || null,
      endAcademicYearId: form.endAcademicYearId || null,
      effectiveDate: form.effectiveDate || null,
      effectiveThruDate: form.effectiveThruDate || null,
      batchYearId: form.batchYearId || null,
    };
    try {
      if (editId) {
        await api.updateBatch(form.programId, editId, req);
      } else {
        await api.createBatch(form.programId, req);
      }
      setShowModal(false);
      await refresh();
      toast.success(editId ? 'Batch updated.' : 'Batch created.');
    } catch (e: any) {
      setFormError(e?.message ?? 'Failed to save batch.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(b: BatchGrid) {
    if (!confirm(`Delete batch “${b.name}”? This cannot be undone.`)) return;
    setError(null);
    try {
      await api.deleteBatch(b.programId, b.id);
      await refresh();
      toast.success('Batch deleted.');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to delete batch.';
      setError(msg);
      toast.error(msg);
    }
  }

  return (
    <OrgLayout>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>Batch</h1>
        <button className="primary" onClick={openCreate} disabled={!hasPrograms}>+ Add batch</button>
      </div>

      {!hasPrograms && batches && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="muted">
            Create an <Link to={`/org/${orgId}/institutions`}>institution</Link>,{' '}
            <Link to={`/org/${orgId}/departments`}>department</Link> and{' '}
            <Link to={`/org/${orgId}/programs`}>program</Link> first — batches live under a program.
          </div>
        </div>
      )}

      {error && <div className="error" style={{ margin: '12px 0' }}>{error}</div>}

      <div className="card" style={{ marginTop: 16 }}>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 200px' }}>
            <div className="muted">Institution</div>
            <select value={filter.institutionId} onChange={e => setFilterField('institutionId', e.target.value)}>
              <option value="">All</option>
              {insts.map(i => <option key={i.id} value={i.id}>{i.code} — {i.name}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 200px' }}>
            <div className="muted">Department</div>
            <select value={filter.departmentId} disabled={!filter.institutionId}
                    onChange={e => setFilterField('departmentId', e.target.value)}>
              <option value="">All</option>
              {deptsForFilter.map(d => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 200px' }}>
            <div className="muted">Program</div>
            <select value={filter.programId} disabled={!filter.departmentId}
                    onChange={e => setFilterField('programId', e.target.value)}>
              <option value="">All</option>
              {programsForFilter.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
            </select>
          </label>
          {(filter.institutionId || filter.departmentId || filter.programId) && (
            <button onClick={() => setFilter({ institutionId: '', departmentId: '', programId: '' })}>
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16, padding: 0, overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 56 }}>S.no</th>
              <th>Institution</th>
              <th>Department</th>
              <th>Program</th>
              <th>Batch Code</th>
              <th>Batch Name</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th style={{ width: 180 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!batches && <tr><td colSpan={8} className="muted">Loading…</td></tr>}
            {batches && filteredBatches.length === 0 && (
              <tr><td colSpan={9} className="muted">
                {batches.length === 0 ? 'No batches yet.' : 'No batches match the selected filters.'}
              </td></tr>
            )}
            {filteredBatches.map((b, idx) => (
              <tr key={b.id}>
                <td>{idx + 1}</td>
                <td>{b.institutionCode}</td>
                <td>{b.departmentCode}</td>
                <td>{b.programCode}</td>
                <td className="slug">{b.code}</td>
                <td style={{ fontWeight: 600 }}>{b.name}</td>
                <td>{fmtDate(b.effectiveDate)}</td>
                <td>{fmtDate(b.effectiveThruDate)}</td>
                <td>
                  <button className="link-btn" onClick={() => openEdit(b)}>Edit</button>
                  <button className="link-btn" style={{ color: 'var(--danger)' }} onClick={() => remove(b)}>Delete</button>
                  <RecordInfoButton entity="batches" id={b.id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal title={editId ? 'Edit batch' : 'Create batch'} onClose={() => setShowModal(false)}>
          <form onSubmit={submit} className="col" style={{ marginTop: 16 }}>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: 1 }}>
                <div className="muted">Institution *</div>
                <select value={form.institutionId} required disabled={!!editId}
                        onChange={e => setField('institutionId', e.target.value)}>
                  <option value="" disabled>Select…</option>
                  {insts.map(i => <option key={i.id} value={i.id}>{i.code} — {i.name}</option>)}
                </select>
              </label>
              <label style={{ flex: 1 }}>
                <div className="muted">Department *</div>
                <select value={form.departmentId} required disabled={!!editId || !form.institutionId}
                        onChange={e => setField('departmentId', e.target.value)}>
                  <option value="" disabled>{form.institutionId ? 'Select…' : 'Pick institution first'}</option>
                  {deptsForForm.map(d => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
                </select>
              </label>
            </div>
            <label>
              <div className="muted">Program *</div>
              <select value={form.programId} required disabled={!!editId || !form.departmentId}
                      onChange={e => setField('programId', e.target.value)}>
                <option value="" disabled>{form.departmentId ? 'Select…' : 'Pick department first'}</option>
                {programsForForm.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
              </select>
            </label>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: 1 }}>
                <div className="muted">Start Act Date *</div>
                <select value={form.startAcademicYearId} required
                        onChange={e => setField('startAcademicYearId', e.target.value)}>
                  <option value="" disabled>--Choose Start Act Date--</option>
                  {academicYears.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </label>
              <label style={{ flex: 1 }}>
                <div className="muted">End Act Date *</div>
                <select value={form.endAcademicYearId} required
                        onChange={e => setField('endAcademicYearId', e.target.value)}>
                  <option value="" disabled>--Choose End Act Date--</option>
                  {academicYears.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </label>
            </div>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: '0 0 160px' }}>
                <div className="muted">Batch Code *</div>
                <input value={form.code} required maxLength={32}
                       onChange={e => setField('code', e.target.value)} placeholder="CSE-A" />
              </label>
              <label style={{ flex: 1 }}>
                <div className="muted">Batch Name *</div>
                <input value={form.name} required maxLength={200}
                       onChange={e => setField('name', e.target.value)} placeholder="CSE 2024 Batch" />
              </label>
            </div>
            <label>
              <div className="muted">Batch Year *</div>
              <select value={form.batchYearId} required onChange={e => pickBatchYear(e.target.value)}>
                <option value="" disabled>
                  {batchYears.length ? 'Select batch year…' : 'No batch years — add them first'}
                </option>
                {batchYears.map(y => (
                  <option key={y.id} value={y.id}>
                    {y.name} ({fmtDate(y.startDate)} → {fmtDate(y.endDate)})
                  </option>
                ))}
              </select>
              {selectedBatchYear && (
                <div className="muted" style={{ marginTop: 4 }}>
                  Start date {fmtDate(selectedBatchYear.startDate)} · End date {fmtDate(selectedBatchYear.endDate)} — taken from this batch year.
                </div>
              )}
            </label>
            {batchYears.length === 0 && (
              <div className="muted">
                No batch years yet — add them on the{' '}
                <Link to={`/org/${orgId}/batch-years`}>Batch Year</Link> page first.
              </div>
            )}
            {academicYears.length === 0 && (
              <div className="muted">
                No academic years yet — add them on the{' '}
                <Link to={`/org/${orgId}/academic-years`}>Academic Year</Link> page first.
              </div>
            )}
            {formError && <div className="error">{formError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={busy}>
                {busy ? 'Saving…' : editId ? 'Save changes' : 'Create batch'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </OrgLayout>
  );
}
